/**
 * useResources — resources + group data hook backed by @v0y4ge/api-client.
 * No seed data in the UI. All data comes from the contract layer.
 *
 * Restored 2026-07-03 (Claude Code push-back, drop blocker 1a): mirrors the
 * useStaff.ts fix — real create/update/delete/duplicate calls against the
 * canonical `Resource`/`ResourceGroup` contract types, with an optimistic
 * local fallback only for the preview harness's fixture stub.
 */
import { useState, useEffect } from "react";
import { T } from "@v0y4ge/jrni-ui";
import type { Resource as CanonicalResource, ResourceGroup as CanonicalResourceGroup } from "@v0y4ge/api-contracts";
import { logUpsert, logDelete } from "../../lib/audit";
import { can, onTenantChange, onTenantSwitching } from "../../shell/access";
import {
  listResources, listResourceGroups,
  createResource, updateResource, deleteResource, duplicateResource,
  createResourceGroup, updateResourceGroup, deleteResourceGroup, duplicateResourceGroup,
  type StudioResourcesRequestOptions,
} from "@v0y4ge/api-client";

// baseUrl-carrying options required by every resources choke-point (packet #3 RC1).
// MSW intercepts by path in design-time, so a placeholder base is fine here; the
// real base is wired on deploy.
const REQUEST_OPTS: StudioResourcesRequestOptions = {
  baseUrl: (typeof import.meta !== "undefined" && (import.meta as { env?: Record<string, string> }).env?.VITE_SETTINGS_API_URL) || "/api",
  surface: "settings",
};

export type Resource = Partial<CanonicalResource> & {
  id: string; name: string; kind: string; type: string; color: string;
  status: string; channels: string[]; groups: string[]; location?: string; imageUrl?: string;
};
export type ResourceGroup = Partial<CanonicalResourceGroup> & {
  id: string; name: string; description: string; hue: string; status: string;
  services: string[]; updated?: string;
};

const RES_KINDS: Record<string, { label: string; labelKey: string; icon: string }> = {
  object: { label: "Object", labelKey: "resources.useResources.object", icon: "📦" },
  place:  { label: "Place", labelKey: "resources.useResources.place", icon: "🏠" },
  device: { label: "Device", labelKey: "resources.useResources.device", icon: "💻" },
};
export { RES_KINDS };
export function resourceKindLabel(kind: string, translate: (key: string, fallback: string) => string): string {
  const meta = RES_KINDS[kind];
  return meta ? translate(meta.labelKey, meta.label) : kind;
}

function toResource(r: Record<string, any>): Resource {
  return {
    id: r.id, name: r.name ?? "", kind: r.kind ?? "object", type: r.type ?? r.resource_type ?? "",
    color: r.color ?? r.hue ?? T.p1, status: r.status ?? "active",
    channels: r.channels ?? [], groups: r.groups ?? r.groupIds ?? [],
    location: r.location ?? r.locationName ?? r.locationId ?? undefined, imageUrl: r.imageUrl ?? r.image_url ?? r.photo ?? undefined,
  };
}
function toGroup(g: Record<string, any>): ResourceGroup {
  return {
    id: g.id, name: g.name ?? "", description: g.description ?? "", hue: g.hue ?? g.color ?? T.p1,
    status: g.status ?? "published", services: g.services ?? [], updated: g.updatedAt ?? g.updated_at ?? undefined,
  };
}

/**
 * Shared cache — hydrated from the typed client; mutations call the real
 * create/update/delete/duplicate operations and reconcile the response.
 */
interface ResState { items: Resource[]; groups: ResourceGroup[]; }
let _cache: ResState | null = null;
let _loading = false;
/** Set only when a list read FAILED — never for an empty list (2026-07-31 rule;
 *  screens pass this to <LoadState> so an outage cannot look like "no resources"). */
let _error: string | null = null;
const _listeners = new Set<() => void>();
const _notify = () => _listeners.forEach((fn) => fn());
onTenantSwitching(() => { _cache = null; _loading = false; _notify(); });
onTenantChange(() => { if (can("locations.read")) hydrate(); }); // admin/catalog/resources + groups — gated (docket 2026-09-17); Resources rail is locations.read
function _set(patch: Partial<ResState>) {
  _cache = { items: _cache?.items ?? [], groups: _cache?.groups ?? [], ...patch };
  _notify();
}
function hydrate(done?: () => void) {
  if (_loading) { done?.(); return; }
  _loading = true;
  /* Failed read ≠ empty list (2026-07-31 rule — see shell/composites LoadState). */
  _error = null;
  Promise.all([
    (typeof listResources === "function" ? listResources({ limit: String(200) }, REQUEST_OPTS) : Promise.resolve({ data: [], resources: [] })).catch((e) => { console.warn("listResources failed:", e); _error = "Couldn't load resources."; return { data: [], resources: [] }; }),
    (typeof listResourceGroups === "function" ? listResourceGroups({ limit: String(100) }, REQUEST_OPTS) : Promise.resolve({ data: [], groups: [] })).catch((e) => { console.warn("listResourceGroups failed:", e); _error = _error ? "Couldn't load resources or groups." : "Couldn't load resource groups."; return { data: [], groups: [] }; }),
  ]).then(([rr, gr]) => {
    const rs = ((rr as any).data ?? (rr as any).resources ?? []) as any[];
    const gs = ((gr as any).data ?? (gr as any).groups ?? []) as any[];
    _cache = { items: rs.map(toResource), groups: gs.map(toGroup) };
    _loading = false;
    _notify();
    done?.();
  }).catch(() => { _cache = { items: [], groups: [] }; _loading = false; _notify(); done?.(); });
}

export function useResources() {
  const [, force] = useState(0);
  const [loading, setLoading] = useState(_cache === null);

  useEffect(() => {
    const fn = () => force((n) => n + 1);
    _listeners.add(fn);
    if (_cache === null && !_loading) hydrate(() => setLoading(false));
    else setLoading(false);
    return () => { _listeners.delete(fn); };
  }, []);

  const state = _cache ?? { items: [], groups: [] };
  return {
    items: state.items,
    groups: state.groups,
    // Back-compat alias for read-only consumers.
    resources: state.items,
    loading,
    /** Failed read — pass to <LoadState>; never render an empty list over this. */
    error: _error,
    upsertItem: async (r: Resource) => {
      const items = _cache?.items ?? [];
      const isNew = !items.some((x) => x.id === r.id);
      /* `photo` was MISSING here (2026-08-07, Pat): the editor's ImageDropField
         writes `photo`, the payload only carried `imageUrl`, so an uploaded
         resource photo was dropped on save. Both are sent, resolved from
         whichever the form holds. */
      const input = { name: r.name, kind: r.kind, type: r.type, color: r.color, status: r.status, channels: r.channels, groups: r.groups, locationId: r.location, imageUrl: r.imageUrl ?? (r as { photo?: string }).photo, photo: (r as { photo?: string }).photo ?? r.imageUrl };
      let saved: Resource = r;
      // Addendum item 7 class (same as useStaff): await-before-commit — no
      // optimistic local apply behind a success toast.
      if (isNew && typeof createResource === "function") saved = toResource(await createResource(input as any, REQUEST_OPTS));
      else if (!isNew && typeof updateResource === "function") saved = toResource(await updateResource(r.id, input as any, REQUEST_OPTS));
      logUpsert("resource", isNew ? null : (items.find((x) => x.id === r.id) as unknown as Record<string, unknown>), saved as unknown as Record<string, unknown>, { name: saved.name });
      const next = isNew ? [saved, ...items] : items.map((x) => (x.id === r.id ? saved : x));
      _set({ items: next });
    },
    removeItem: async (id: string) => {
      if (typeof deleteResource === "function") await deleteResource(id, REQUEST_OPTS);
      const prev = (_cache?.items ?? []).find((x) => x.id === id);
      if (prev) logDelete("resource", prev as unknown as Record<string, unknown>, { name: prev.name });
      _set({ items: (_cache?.items ?? []).filter((x) => x.id !== id) });
    },
    duplicateItem: async (id: string) => {
      try {
        if (typeof duplicateResource === "function") {
          const copy = toResource(await duplicateResource(id, REQUEST_OPTS));
          _set({ items: [copy, ...(_cache?.items ?? [])] });
          return copy;
        }
      } catch (e) { console.warn("duplicateResource failed:", e); }
      return null;
    },
    upsertGroup: async (g: ResourceGroup) => {
      const groups = _cache?.groups ?? [];
      const isNew = !groups.some((x) => x.id === g.id);
      const input = { name: g.name, description: g.description, hue: g.hue, status: g.status, services: g.services };
      let saved: ResourceGroup = g;
      if (isNew && typeof createResourceGroup === "function") saved = toGroup(await createResourceGroup(input as any, REQUEST_OPTS));
      else if (!isNew && typeof updateResourceGroup === "function") saved = toGroup(await updateResourceGroup(g.id, input as any, REQUEST_OPTS));
      logUpsert("resourceGroup", isNew ? null : (groups.find((x) => x.id === g.id) as unknown as Record<string, unknown>), saved as unknown as Record<string, unknown>, { name: saved.name });
      const next = isNew ? [saved, ...groups] : groups.map((x) => (x.id === g.id ? saved : x));
      _set({ groups: next });
    },
    removeGroup: async (id: string) => {
      if (typeof deleteResourceGroup === "function") await deleteResourceGroup(id, REQUEST_OPTS);
      const prev = (_cache?.groups ?? []).find((x) => x.id === id);
      if (prev) logDelete("resourceGroup", prev as unknown as Record<string, unknown>, { name: prev.name });
      _set({ groups: (_cache?.groups ?? []).filter((x) => x.id !== id) });
    },
    duplicateGroup: async (id: string) => {
      try {
        if (typeof duplicateResourceGroup === "function") {
          const copy = toGroup(await duplicateResourceGroup(id, REQUEST_OPTS));
          _set({ groups: [copy, ...(_cache?.groups ?? [])] });
          return copy;
        }
      } catch (e) { console.warn("duplicateResourceGroup failed:", e); }
      return null;
    },
    reload: () => { _cache = null; hydrate(); },
  };
}
